/**
 * 件名カード: 編集 / AI 再生成（3 候補） / AI 壁打ち / コピー
 */
import type { MailSection } from '@goenhub/shared';
import { httpsCallable } from 'firebase/functions';
import { Check, Copy, Edit3, Lightbulb, RefreshCw, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { BrainstormDialog, type BrainstormResult } from './BrainstormDialog';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { functions } from '@/lib/firebase';

interface MailContext {
  currentSubject: string;
  sections: MailSection[];
  overview?: string;
  lang?: string;
}

const regenerateSubjectFn = httpsCallable<
  { context: MailContext; instruction?: string },
  { ok: boolean; candidates: string[]; reasoning: string; modelUsed: string }
>(functions, 'regenerateSubject');

const brainstormSubjectFn = httpsCallable<
  { context: MailContext; userFeedback: string },
  BrainstormResult & { ok: boolean; modelUsed: string }
>(functions, 'brainstormSubject');

interface SubjectCardProps {
  subject: string;
  context: MailContext;
  onChange: (next: string) => void;
  /**
   * 編集中（未確定）の入力を親へ伝える。編集を終えたら null。
   * 親の保存ボタンが未確定の入力を自動で確定するために使う（spec_mail_system.md B-19.3）。
   */
  onDraftChange?: (draft: string | null) => void;
}

export function SubjectCard({ subject, context, onChange, onDraftChange }: SubjectCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(subject);
  const [regenerating, setRegenerating] = useState(false);
  const [candidates, setCandidates] = useState<string[] | null>(null);
  const [brainstormOpen, setBrainstormOpen] = useState(false);

  const startEdit = () => {
    setDraft(subject);
    setEditing(true);
    onDraftChange?.(subject);
  };

  const changeDraft = (next: string) => {
    setDraft(next);
    onDraftChange?.(next);
  };

  const commitEdit = () => {
    onChange(draft.trim());
    setEditing(false);
    onDraftChange?.(null);
  };

  const cancelEdit = () => {
    setDraft(subject);
    setEditing(false);
    onDraftChange?.(null);
  };

  // 編集中のままカードが消えた（表示切替・クリア等）場合は未確定の入力を破棄する
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  useEffect(() => () => onDraftChangeRef.current?.(null), []);

  // 編集窓の外をクリックしたら ✅ と同じく「確定」扱いに（UX: ✅ 押し忘れ防止）。
  // カード内（✅/✕ 含む）クリックは確定せず各ボタンに委ねるクリックアウェイ方式。
  const cardRef = useRef<HTMLDivElement>(null);
  const commitEditRef = useRef(commitEdit);
  commitEditRef.current = commitEdit;
  useEffect(() => {
    if (!editing) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = cardRef.current;
      if (el && !el.contains(e.target as Node)) {
        commitEditRef.current();
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [editing]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    setCandidates(null);
    try {
      const res = await regenerateSubjectFn({ context });
      setCandidates(res.data.candidates);
    } catch (err) {
      toast.error(`件名再生成失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRegenerating(false);
    }
  };

  const adoptCandidate = (c: string) => {
    onChange(c);
    setCandidates(null);
    toast.success('件名を更新しました');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(subject);
      toast.success('件名をコピーしました');
    } catch {
      toast.error('コピーに失敗');
    }
  };

  const runBrainstorm = async (userFeedback: string): Promise<BrainstormResult> => {
    try {
      const res = await brainstormSubjectFn({ context, userFeedback });
      return {
        proposal: res.data.proposal,
        alternatives: res.data.alternatives,
        reasoning: res.data.reasoning,
      };
    } catch (err) {
      toast.error(`壁打ち失敗: ${err instanceof Error ? err.message : String(err)}`);
      throw err;
    }
  };

  return (
    <div ref={cardRef} className="rounded bg-[var(--bg-elevated)] border border-[var(--border)]">
      <div className="flex items-center justify-between p-2 bg-[var(--bg-hover)] border-b border-[var(--border)] rounded-t text-xs">
        <Label className="!mb-0 font-semibold">件名</Label>
        <div className="flex gap-0.5">
          {!editing && (
            <>
              <Button type="button" variant="ghost" size="icon" onClick={handleCopy} title="コピー">
                <Copy className="size-3" />
              </Button>
              <Button type="button" variant="ghost" size="icon" onClick={startEdit} title="編集">
                <Edit3 className="size-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleRegenerate}
                disabled={regenerating}
                title="AI 再生成"
              >
                {regenerating ? <Spinner className="size-3" /> : <RefreshCw className="size-3" />}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setBrainstormOpen(true)}
                title="AI 壁打ち"
              >
                <Lightbulb className="size-3" />
              </Button>
            </>
          )}
          {editing && (
            <>
              <Button type="button" variant="ghost" size="icon" onClick={commitEdit} title="確定">
                <Check className="size-3" />
              </Button>
              <Button type="button" variant="ghost" size="icon" onClick={cancelEdit} title="取消">
                <X className="size-3" />
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="p-3">
        {editing ? (
          <Input
            value={draft}
            onChange={(e) => changeDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            autoFocus
          />
        ) : (
          <div className="font-semibold text-[14px]">{subject || '(件名なし)'}</div>
        )}

        {candidates && (
          <div className="mt-3 pt-3 border-t border-[var(--border)] space-y-2">
            <Label className="text-xs">Claude 提案候補</Label>
            {candidates.map((c, i) => (
              <div
                key={i}
                className="flex items-center gap-2 p-2 rounded bg-[var(--bg-surface)] border border-[var(--border)] hover:border-[var(--accent)] transition-colors"
              >
                <span className="text-[13px] flex-1">{c}</span>
                <Button type="button" variant="primary" size="sm" onClick={() => adoptCandidate(c)}>
                  採用
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCandidates(null)}
              className="w-full"
            >
              閉じる
            </Button>
          </div>
        )}
      </div>

      <BrainstormDialog
        open={brainstormOpen}
        onOpenChange={setBrainstormOpen}
        title="件名の壁打ち"
        currentContent={subject}
        runBrainstorm={runBrainstorm}
        onAdopt={(c) => {
          onChange(c);
          toast.success('件名を更新しました');
        }}
      />
    </div>
  );
}
